import { collection, getDocs, query, orderBy, limit, where, setDoc, doc, getDoc, addDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { MindsharePost, SubnetMindshare, SubnetMindshareHistory, MindshareHistoryEntry } from '../types/mindshare';
import { subnetNames } from '../utils/subnetNames';
import { cacheService } from './cacheService';
import { updateCurrentMindshare } from './currentMindshareService';
import { clearAllCaches, getCorrectSubnetName } from '../utils/cacheUtils';

// Collection references
const postsCollection = collection(db, 'mindshare_posts');
const subnetMindshareCollection = collection(db, 'subnet_mindshare');

// Cache keys
const CACHE_KEYS = {
  MINDSHARE_DATA: 'mindshare_data',
  MINDSHARE_HISTORY: 'mindshare_history_'
};

// Get all posts with pagination
export const getPosts = async (limitCount = 20): Promise<MindsharePost[]> => {
  try {
    const q = query(postsCollection, orderBy('timestamp', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);
    
    const posts: MindsharePost[] = [];
    for (const docSnapshot of querySnapshot.docs) {
      const postData = docSnapshot.data();
      
      posts.push({
        id: docSnapshot.id,
        title: postData.title || '',
        content: postData.content || '',
        author: postData.author || 'Anonymous',
        authorAddress: postData.authorAddress || '',
        timestamp: postData.timestamp || Date.now(),
        likes: postData.likes || 0,
        tags: postData.tags || []
      });
    }
    
    return posts;
  } catch (error) {
    console.error('Error fetching mindshare posts:', error);
    return [];
  }
};

// Get posts by tag
export const getPostsByTag = async (tag: string, limitCount = 20): Promise<MindsharePost[]> => {
  try {
    const q = query(
      postsCollection, 
      where('tags', 'array-contains', tag),
      orderBy('timestamp', 'desc'), 
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MindsharePost));
  } catch (error) {
    console.error('Error fetching posts by tag:', error);
    return [];
  }
};

// Get subnet mindshare data
export const getSubnetMindshare = async (): Promise<SubnetMindshare[]> => {
  try {
    // Always clear cache to ensure fresh data
    clearAllCaches();
    
    // Fetch fresh data from Firestore
    const q = query(subnetMindshareCollection, orderBy('mindsharePercentage', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const mindshareData: SubnetMindshare[] = [];
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data() as SubnetMindshare;
      const netuid = parseInt(docSnapshot.id);
      
      // Get the correct subnet name using our utility function
      const correctName = getCorrectSubnetName(netuid, subnetNames);
      
      mindshareData.push({
        ...data,
        netuid,
        name: correctName // Ensure we use the up-to-date name
      });
    }
    
    return mindshareData;
  } catch (error) {
    console.error('Error fetching subnet mindshare data:', error);
    return [];
  }
};

// Save subnet mindshare data and update history
export const saveSubnetMindshare = async (mindshareData: SubnetMindshare & { _forceHistoryUpdate?: boolean }): Promise<boolean> => {
  try {
    // Extract and remove the _forceHistoryUpdate flag
    const forceHistoryUpdate = mindshareData._forceHistoryUpdate;
    const { _forceHistoryUpdate, ...cleanData } = mindshareData;
    
    // Use current timestamp for lastUpdated
    const currentTimestamp = new Date().toISOString();
    
    // Get the correct subnet name using our utility function
    const correctName = getCorrectSubnetName(cleanData.netuid, subnetNames);
    
    // Save current mindshare data with server timestamp and correct name
    await setDoc(doc(subnetMindshareCollection, cleanData.netuid.toString()), {
      ...cleanData,
      name: correctName, // Ensure we use the correct name
      lastUpdated: currentTimestamp
    });
    
    // Check if we should update history
    const historyCollectionRef = collection(db, 'subnet_history', cleanData.netuid.toString(), 'entries');
    
    // Get the most recent history entry
    const recentQuery = query(
      historyCollectionRef,
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    const recentSnapshot = await getDocs(recentQuery);
    const lastEntry = recentSnapshot.docs[0]?.data() as MindshareHistoryEntry | undefined;
    
    let shouldUpdateHistory = forceHistoryUpdate === true;
    
    if (!shouldUpdateHistory && lastEntry) {
      const lastUpdateTime = new Date(lastEntry.timestamp);
      const timeSinceLastUpdate = Date.now() - lastUpdateTime.getTime();
      
      // Only update history if more than 15 minutes have passed (reduced from 60 minutes)
      if (timeSinceLastUpdate < 15 * 60 * 1000) {
        console.log(`Skipping history update for subnet ${cleanData.netuid} - last update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago`);
      } else {
        shouldUpdateHistory = true;
        console.log(`History update needed for subnet ${cleanData.netuid} - last update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago`);
      }
    } else if (!lastEntry) {
      shouldUpdateHistory = true;
      console.log(`No previous history entry found for subnet ${cleanData.netuid}, creating first entry`);
    }
    
    // If enough time has passed or no previous entry exists, update history
    if (shouldUpdateHistory) {
      console.log(`Updating history for subnet ${cleanData.netuid}`);
      await addHistoryEntry({
        ...cleanData,
        name: correctName, // Ensure we use the correct name
        lastUpdated: currentTimestamp
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error saving subnet mindshare data:', error);
    return false;
  }
};

// Add a new history entry to the subnet's history subcollection
const addHistoryEntry = async (mindshareData: SubnetMindshare): Promise<void> => {
  try {
    // Create a reference to the history subcollection for this subnet
    const historyCollectionRef = collection(db, 'subnet_history', mindshareData.netuid.toString(), 'entries');
    
    // Get the previous entry to calculate percent change
    const prevQuery = query(
      historyCollectionRef,
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    const prevSnapshot = await getDocs(prevQuery);
    const prevEntry = prevSnapshot.docs[0]?.data() as MindshareHistoryEntry | undefined;
    
    // Calculate percent change if previous entry exists
    let percentChange = 0;
    if (prevEntry && prevEntry.mindsharePercentage > 0) {
      percentChange = ((mindshareData.mindsharePercentage - prevEntry.mindsharePercentage) / prevEntry.mindsharePercentage) * 100;
    }
    
    // Create the new history entry
    const newHistoryEntry: MindshareHistoryEntry = {
      timestamp: mindshareData.lastUpdated,
      mindsharePercentage: mindshareData.mindsharePercentage,
      twitterEngagement: mindshareData.twitterEngagement,
      marketCapScore: mindshareData.marketCapScore,
      rankScore: mindshareData.rankScore,
      volumeScore: mindshareData.volumeScore,
      totalScore: mindshareData.totalScore,
      percentChange: percentChange
    };
    
    // Add the entry as a new document in the subcollection
    // Use the timestamp as the document ID to ensure uniqueness
    const docId = new Date(mindshareData.lastUpdated).getTime().toString();
    await setDoc(doc(historyCollectionRef, docId), newHistoryEntry);
    
    // Also update the subnet metadata document in the subnet_history collection
    // Get the correct subnet name using our utility function
    const correctName = getCorrectSubnetName(mindshareData.netuid, subnetNames);
    
    await setDoc(doc(db, 'subnet_history', mindshareData.netuid.toString()), {
      netuid: mindshareData.netuid,
      name: correctName, // Always use the correct name
      lastUpdated: mindshareData.lastUpdated
    }, { merge: true });
    
    // Update the current_mindshare collection with this latest entry
    await updateCurrentMindshare(mindshareData.netuid, newHistoryEntry);
    
    console.log(`Added history entry for subnet ${mindshareData.netuid} at ${mindshareData.lastUpdated} with percent change: ${percentChange.toFixed(2)}%`);
  } catch (error) {
    console.error('Error adding history entry:', error);
  }
};

// Get mindshare history for a subnet using subcollection
export const getMindshareHistory = async (netuid: number): Promise<SubnetMindshareHistory | null> => {
  try {
    // Clear cache before fetching to ensure fresh data
    clearAllCaches();
    
    // Get subnet metadata
    const subnetDocRef = doc(db, 'subnet_history', netuid.toString());
    const subnetDocSnap = await getDoc(subnetDocRef);
    
    if (!subnetDocSnap.exists()) {
      console.log(`No history metadata found for subnet ${netuid}`);
      return null;
    }
    
    // Get all history entries from the correct path: subnet_history/{netuid}/history
    const historyCollectionRef = collection(db, 'subnet_history', netuid.toString(), 'entries');
    const q = query(
      historyCollectionRef,
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    const historyEntries: MindshareHistoryEntry[] = [];
    querySnapshot.forEach(doc => {
      historyEntries.push(doc.data() as MindshareHistoryEntry);
    });
    
    // Get the correct subnet name using our utility function
    const correctName = getCorrectSubnetName(netuid, subnetNames);
    
    const result = {
      netuid: netuid,
      name: correctName, // Use the correct name
      history: historyEntries
    };
    
    return result;
  } catch (error) {
    console.error(`Error fetching mindshare history for subnet ${netuid}:`, error);
    return null;
  }
};

// Get mindshare history for all subnets
export const getAllMindshareHistory = async (): Promise<SubnetMindshareHistory[]> => {
  try {
    // Get all subnet metadata documents
    const subnetsQuery = query(collection(db, 'subnet_history'));
    const subnetsSnapshot = await getDocs(subnetsQuery);
    
    const historyPromises = subnetsSnapshot.docs.map(async (subnetDoc) => {
      const netuid = parseInt(subnetDoc.id);
      return getMindshareHistory(netuid);
    });
    
    const results = await Promise.all(historyPromises);
    return results.filter((result): result is SubnetMindshareHistory => result !== null);
  } catch (error) {
    console.error('Error fetching all mindshare history:', error);
    return [];
  }
};

// Get top subnets by mindshare
export const getTopSubnetsByMindshare = async (limit = 10): Promise<SubnetMindshare[]> => {
  try {
    const q = query(
      subnetMindshareCollection, 
      orderBy('mindsharePercentage', 'desc'), 
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    
    const topSubnets: SubnetMindshare[] = [];
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data() as SubnetMindshare;
      const netuid = parseInt(docSnapshot.id);
      
      // Get the correct subnet name using our utility function
      const correctName = getCorrectSubnetName(netuid, subnetNames);
      
      topSubnets.push({
        ...data,
        netuid,
        name: correctName // Ensure we use the up-to-date name
      });
    }
    
    return topSubnets;
  } catch (error) {
    console.error('Error fetching top subnets by mindshare:', error);
    return [];
  }
};

// Get subnets with the most growth in mindshare
export const getGrowingSubnets = async (limit = 5): Promise<{subnet: SubnetMindshare, growth: number}[]> => {
  try {
    // Get all subnet histories from last 24 hours
    const oneDayAgo = new Date();
    oneDayAgo.setHours(oneDayAgo.getHours() - 24);
    
    const allHistories = await getAllMindshareHistory();
    
    // Calculate growth for each subnet
    const growthData = allHistories
      .filter(history => history.history.length >= 2)
      .map(history => {
        // Sort by timestamp (oldest to newest)
        const sortedEntries = [...history.history]
          .filter(entry => new Date(entry.timestamp) >= oneDayAgo)
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        if (sortedEntries.length < 2) return null;
        
        // Get oldest and newest entries
        const oldestEntry = sortedEntries[0];
        const newestEntry = sortedEntries[sortedEntries.length - 1];
        
        // Calculate growth percentage
        const oldValue = oldestEntry.mindsharePercentage;
        const newValue = newestEntry.mindsharePercentage;
        
        let growthPercentage = 0;
        if (oldValue > 0) {
          growthPercentage = ((newValue - oldValue) / oldValue) * 100;
        }
        
        // Get current mindshare data
        const currentData: SubnetMindshare = {
          netuid: history.netuid,
          name: history.name,
          mindsharePercentage: newestEntry.mindsharePercentage,
          twitterEngagement: newestEntry.twitterEngagement,
          marketCapScore: newestEntry.marketCapScore,
          rankScore: newestEntry.rankScore,
          volumeScore: newestEntry.volumeScore,
          totalScore: newestEntry.totalScore,
          lastUpdated: newestEntry.timestamp
        };
        
        return {
          subnet: currentData,
          growth: growthPercentage
        };
      })
      .filter((item): item is NonNullable<typeof growthData[0]> => item !== null)
      .sort((a, b) => b.growth - a.growth);
    
    return growthData.slice(0, limit);
  } catch (error) {
    console.error('Error fetching growing subnets:', error);
    return [];
  }
};

// Create a new post
export const createPost = async (post: Omit<MindsharePost, 'id'>): Promise<string | null> => {
  try {
    const docRef = doc(postsCollection);
    await setDoc(docRef, {
      ...post,
      timestamp: Date.now()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating post:', error);
    return null;
  }
};

// Like a post
export const likePost = async (postId: string): Promise<boolean> => {
  try {
    const postRef = doc(postsCollection, postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      return false;
    }
    
    const postData = postSnap.data();
    const currentLikes = postData.likes || 0;
    
    await setDoc(postRef, {
      ...postData,
      likes: currentLikes + 1
    });
    
    return true;
  } catch (error) {
    console.error('Error liking post:', error);
    return false;
  }
};

// Get posts related to a specific subnet
export const getPostsBySubnet = async (netuid: number, limitCount = 10): Promise<MindsharePost[]> => {
  try {
    const subnetName = getCorrectSubnetName(netuid, subnetNames);
    
    // Search for posts with subnet name or number in tags
    const q = query(
      postsCollection,
      where('tags', 'array-contains-any', [subnetName, `SN${netuid}`, `${netuid}`]),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MindsharePost));
  } catch (error) {
    console.error(`Error fetching posts for subnet ${netuid}:`, error);
    return [];
  }
};